import { TnProvisionFormSettings } from './tn-provision-form-settings';

export interface ServiceTypeSettings {
  tnProvisionFormSettings?: TnProvisionFormSettings;
}
export interface ServiceType {
  type: string;
  name: string;
  category: string;
  position: number;
  flags: string[];
  scope: string;
  regions: string[]
  defaultSettings?: ServiceTypeSettings;
}

export enum ServiceTypeScopeEnum {
  CUSTOMER = 'customer',
  RESELLER = 'reseller',
}

export enum ServiceTypeRegionEnum {
  EU = 'eu',
  US = 'us',
}

export type ServiceTypeScope = ServiceTypeScopeEnum.CUSTOMER | ServiceTypeScopeEnum.RESELLER;
export type ServiceTypeRegion = ServiceTypeRegionEnum.EU | ServiceTypeRegionEnum.US;
export type CreateServiceTypeInput = Partial<ServiceType> & Omit<ServiceType, 'defaultSettings'>;
export type UpdateServiceTypeInput = Partial<ServiceType> & { type: string };

export const PLATFORM_SERVICE_TYPES = {
  BLUE_SIP: { name: 'blue-sip', label: 'Blue SIP' },
  NUSO_SIP: { name: 'nuso-sip', label: 'Orange SIP' },
  GREEN_UC: { name: 'green-uc', label: 'Green UCaaS' },
  IPFAX: { name: 'ipfax', label: 'IP Fax' },
  CLOUD_UC: { name: 'cloud-uc', label: 'Orange UCaas' },
  TEAMS_OC: { name: 'teams-oc', label: 'Teams Operator Connect' },
  CCAAS: { name: 'ccaas', label: 'Connect' },
  BRIDGE_ZOOM: { name: 'bridge-zoom', label: 'Zoom' },
  TEAMS_SIP: { name: 'teams-sip', label: 'Teams SIP Connect' },
  TEAMS_PC: { name: 'teams-pc', label: 'Teams Platform Connect' },
  TEAMS_AD: { name: 'teams-ad', label: 'Teams Active Directory' },
  CARRIER_TRUNK: { name: 'carrier-trunk', label: 'Carrier Trunk' },
  CARRIER_TENANT_TRUNK: { name: 'carrier-tenant-trunk', label: 'Carrier Tenant Trunk' },
  GREEN_RESELLER: { name: 'green-reseller', label: 'Green Reseller' },
  BRIDGE_ZOOM_RESELLER: { name: 'bridge-zoom-reseller', label: 'Green Reseller' },
  TEAMS_RESELLER: { name: 'teams-reseller', label: 'Teams Reseller' },
  RECORDING: { name: 'recording', label: 'Recording' },
  WORKFLOW_DEMO: { name: 'workflow-demo', label: 'Workflow Demo' },
  AI_SERVICES: { name: 'ai-services', label: 'AI Services' },
}

export const BLUE_ORANGE_SERVICE_TYPES = [
  'blue-sip',
  'nuso-sip',
  'cloud-uc',
];

export const SERVICE_TYPES_SUPPORT_RECORDING = [
  'green-uc',
  'cloud-uc',
  'nuso-sip',
  'teams-sip',
  'teams-ad',
  'teams-domain',
  'zoom-domain',
];

export enum AUTHORIZATION_SERVICE_TYPES {
  TEAMS_DOMAIN = 'teams-domain',
  ZOOM_DOMAIN = 'zoom-domain',
}

export const RECORDING_SOURCE_LABELS: Record<string, string> = {
  'teams-domain': 'Microsoft Teams',
  'zoom-domain': 'Zoom',
};

export const TEAMS_SERVICE_TYPES = [
  'teams-sip', 'teams-pc', 'teams-oc', 'teams-ad', 'teams-domain',
];

export const BLUE_SERVICE_TYPES = [
  'blue-sip', 'ipfax'
];

export const EMEA_SERVICE_TYPES = [
  'green-uc',
  'green-reseller',
  'carrier-trunk',
  'carrier-tenant-trunk',
  'bridge-zoom',
  'bridge-zoom-reseller',
  'teams-reseller',
  'recording',
  'teams-ad',
  'teams-sip',
  'teams-pc',
  'teams-domain',
]

export function isBlueSipServiceType(serviceType: string | undefined) {
  return serviceType === 'blue-sip';
}

export function isGreenServiceType(serviceType: string | undefined) {
  return serviceType === 'green-uc';
}

// Ya ya ya, this is orange, not blue. Adding here
// until we move to someplace that may make more sense.
export const ORANGE_SERVICE_TYPES = [
  'cloud-uc', 'nec-uc-m'
];

export const ROUTING_SERVICE_TYPES = [
  'teamm-oc',
  'carrier-trunk',
  'carrier-tenant-trunk',
  'green-uc',
  'bridge-zoom',
];

export const EXCLUDED_VOICE_SERVICE_TYPES_FOR_DID_PROVISIONING = [
  'green-reseller',
  'bridge-zoom-reseller',
  'teams-reseller',
  'teams-pc'
];

export function isOrangeServiceType(serviceType: string) {
  return ORANGE_SERVICE_TYPES.includes(serviceType);
}

export function isIPFaxServiceType(serviceType: string | undefined) {
  return serviceType === 'ipfax';
}

export function isBlueServiceType(serviceType: string | undefined) {
  return !!serviceType && BLUE_SERVICE_TYPES.includes(serviceType);
}


export function servicePlatform(serviceType: string) {
  if (isBlueServiceType(serviceType)) {
    return 'blue';
  }

  if (isGreenServiceType(serviceType)) {
    return 'green';
  }

  if (['bridge-zoom', 'teams-oc', 'teams-ad'].indexOf(serviceType) !== -1) {
    return serviceType;
  }

  return 'orange';
}

// brought over from backend/db-models/customers
export function isBlueSipService(serviceKey: string) {
  return serviceKey.includes('blue') && serviceKey.includes('sip');
}

export function isBlueService(serviceKey: string) {
  return serviceKey.includes('blue') || serviceKey === 'ipfax';
}

export const ResellerToCustomerServiceMatch = {
  'bridge-zoom-reseller': ['bridge-zoom'],
  'carrier-trunk': ['carrier-tenant-trunk'],
  'green-reseller': ['green-uc'],
  'teams-reseller': ['teams-sip', 'teams-pc'],
};

export const CustomerToResellerServiceMatch = {
  'bridge-zoom': 'bridge-zoom-reseller',
  'carrier-tenant-trunk': 'carrier-trunk',
  'green-uc': 'green-reseller',
  'teams-sip': 'teams-reseller',
  'teams-pc': 'teams-reseller',
};

export const CustomerToResellerServiceReferenceMatch: Record<string, string> = {
  'bridge-zoom': 'bridgeZoomResellerServiceId',
  'carrier-tenant-trunk': 'trunkServiceId',
  'green-uc': 'greenResellerServiceId',
  'teams-sip': 'teamsResellerServiceId',
  'teams-pc': 'teamsResellerServiceId',
};

// Maps reseller service types to the child settings field that references them
export const ResellerServiceToReferenceField: Record<string, string> = Object.fromEntries(
  Object.entries(ResellerToCustomerServiceMatch).map(([resellerType, customerType]) => [
    resellerType,
    CustomerToResellerServiceReferenceMatch[customerType[0]],
  ])
);

export function isAiServicesType(serviceType: string) {
  return serviceType === 'ai-services';
}

